
  import React from 'react'
  import { MDXTag } from '@mdx-js/tag'
  import NextSteps from '../components/nextSteps/NextSteps';

export default class MDXContent extends React.Component {
  constructor(props) {
    super(props)
    this.layout = null
  }
  render() {
    const { components, ...props } = this.props

    return <MDXTag
             name="wrapper"
             
             components={components}>
<MDXTag name="h1" components={components}>{`Using Webhooks`}</MDXTag>
<MDXTag name="p" components={components}>{`A webhook (also called a web callback or HTTP push) is a way for the Eventbrite API to provide integrated applications with information. Webhooks delivers information to your applications in real-time, meaning you get an immediate notification.`}</MDXTag>
<MDXTag name="p" components={components}>{`Currently, the webhooks provided by Eventbrite track six objects: Event, Order, Attendee, Organizer, Ticket Class and Venue. You should use these webhooks to ensure that your application remains compliant and compatible with Eventbrite. Make sure to follow the `}<MDXTag name="a" components={components} parentName="p" props={{"href":"/engineering"}}>{`Eventbrite Engineering Blog`}</MDXTag>{` to receive updates about our webhook implementation.`}</MDXTag>
<MDXTag name="p" components={components}>{`To implement webhooks, you need to:`}</MDXTag>
<MDXTag name="ol" components={components}>
<MDXTag name="li" components={components} parentName="ol">
<MDXTag name="p" components={components} parentName="li">{`Make sure you have the necessary prerequisites.`}</MDXTag>
</MDXTag>
<MDXTag name="li" components={components} parentName="ol">
<MDXTag name="p" components={components} parentName="li">{`Create a webhook.`}</MDXTag>
</MDXTag>
<MDXTag name="li" components={components} parentName="ol">
<MDXTag name="p" components={components} parentName="li">{`Test the webhook.`}</MDXTag>
</MDXTag>
</MDXTag>
<MDXTag name="blockquote" components={components}>
<MDXTag name="p" components={components} parentName="blockquote">{`Note: If your website receives webhooks, ensure that the webhooks are sent to a ‘private, unpublished’ URL. Otherwise, your website may be impacted by bogus messages and responses, such as a ‘410 Gone’ response. This 410 response automatically unsubscribes from the endpoint which sent the response.`}</MDXTag>
</MDXTag>
<MDXTag name="hr" components={components}></MDXTag>
<MDXTag name="h3" components={components}>{`Webhooks for the Event object`}</MDXTag>
<MDXTag name="p" components={components}>{`The following Event actions have webhooks:`}</MDXTag>
<MDXTag name="ul" components={components}>
<MDXTag name="li" components={components} parentName="ul">{`Publish`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`Unpublish`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`Create`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`Update`}</MDXTag>
</MDXTag>
<MDXTag name="h3" components={components}>{`Webhooks for the Order Object`}</MDXTag>
<MDXTag name="p" components={components}>{`The following Order actions have webhooks:`}</MDXTag>
<MDXTag name="ul" components={components}>
<MDXTag name="li" components={components} parentName="ul">{`Place`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`Update`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`Refund`}</MDXTag>
</MDXTag>
<MDXTag name="h3" components={components}>{`Webhooks for the Attendee Object`}</MDXTag>
<MDXTag name="p" components={components}>{`The following Attendee actions have webhooks:`}</MDXTag>
<MDXTag name="ul" components={components}>
<MDXTag name="li" components={components} parentName="ul">{`Update`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`Check_in`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`Check_out`}</MDXTag>
</MDXTag>
<MDXTag name="h3" components={components}>{`Webhooks for the Organizer Object`}</MDXTag>
<MDXTag name="p" components={components}>{`The following Organizer actions have webhooks:`}</MDXTag>
<MDXTag name="ul" components={components}>
<MDXTag name="li" components={components} parentName="ul">{`Update`}</MDXTag>
</MDXTag>
<MDXTag name="h3" components={components}>{`Webhooks for the Ticket Class Object`}</MDXTag>
<MDXTag name="p" components={components}>{`The following Ticket Class actions have webhooks:`}</MDXTag>
<MDXTag name="ul" components={components}>
<MDXTag name="li" components={components} parentName="ul">{`Create`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`Delete`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`Update`}</MDXTag>
</MDXTag>
<MDXTag name="h3" components={components}>{`Webhooks for the Venue Object`}</MDXTag>
<MDXTag name="p" components={components}>{`The following Venue actions have webhooks:`}</MDXTag>
<MDXTag name="ul" components={components}>
<MDXTag name="li" components={components} parentName="ul">{`Update`}</MDXTag>
</MDXTag>
<MDXTag name="hr" components={components}></MDXTag>
<MDXTag name="h2" components={components}>{`The Composition of Webhooks`}</MDXTag>
<MDXTag name="p" components={components}>{`Webhooks are constructed as an HTTP request with the following composition:`}</MDXTag>
<MDXTag name="ul" components={components}>
<MDXTag name="li" components={components} parentName="ul">{`Sent via the POST method`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`Application/JSON mime type defined in the headers`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`Valid JSON payload (i.e. the part of transmitted data that is the actual intended message).`}</MDXTag>
</MDXTag>
<MDXTag name="table" components={components}>
<MDXTag name="thead" components={components} parentName="table">
<MDXTag name="tr" components={components} parentName="thead">
<MDXTag name="th" components={components} parentName="tr" props={{"align":null}}>{`Attribute`}</MDXTag>
<MDXTag name="th" components={components} parentName="tr" props={{"align":null}}>{`Description`}</MDXTag>
</MDXTag>
</MDXTag>
<MDXTag name="tbody" components={components} parentName="table">
<MDXTag name="tr" components={components} parentName="tbody">
<MDXTag name="td" components={components} parentName="tr" props={{"align":null}}>{`api_url`}</MDXTag>
<MDXTag name="td" components={components} parentName="tr" props={{"align":null}}>{`Object whose creation or update triggered the webhook`}</MDXTag>
</MDXTag>
<MDXTag name="tr" components={components} parentName="tbody">
<MDXTag name="td" components={components} parentName="tr" props={{"align":null}}>{`config.endpoint_url`}</MDXTag>
<MDXTag name="td" components={components} parentName="tr" props={{"align":null}}>{`Target URL specified when the webhook was created.`}</MDXTag>
</MDXTag>
</MDXTag>
</MDXTag>
<MDXTag name="p" components={components}>{`Example webhook JSON payload:`}</MDXTag>
<MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-json","metastring":null}}>{`{
  "api_url": "https://www.eventbriteapi.com/v3/orders/1234567890/",
  "config": {
    "endpoint_url": "https://myawesomeapp.com/webhook"
  }
}
`}</MDXTag></MDXTag>
<MDXTag name="hr" components={components}></MDXTag>
<MDXTag name="h2" components={components}>{`Prerequisites to Using Webhooks`}</MDXTag>
<MDXTag name="p" components={components}>{`In order to use webhooks, you need the following:`}</MDXTag>
<MDXTag name="ul" components={components}>
<MDXTag name="li" components={components} parentName="ul">{`At least one Event.`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`An Eventbrite API User Key.`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`A registered integrated application`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`A public, online website that can receive webhooks. This can be built in Django, Flask, Rails, Node.js, PHP or any tool for the creation of web pages.`}</MDXTag>
</MDXTag>
<MDXTag name="hr" components={components}></MDXTag>
<MDXTag name="h2" components={components}>{`Creating a Webhook`}</MDXTag>
<MDXTag name="p" components={components}>{`To create a webhook:`}</MDXTag>
<MDXTag name="ol" components={components}>
<MDXTag name="li" components={components} parentName="ol">
<MDXTag name="p" components={components} parentName="li">{`Go to the `}<MDXTag name="a" components={components} parentName="p" props={{"href":"/account-settings/webhooks/add/"}}>{`Webhooks Add`}</MDXTag></MDXTag>
</MDXTag>
<MDXTag name="li" components={components} parentName="ol">
<MDXTag name="p" components={components} parentName="li">{`Choose an Event.`}</MDXTag>
</MDXTag>
<MDXTag name="li" components={components} parentName="ol">
<MDXTag name="p" components={components} parentName="li">{`Enter the URL `}<MDXTag name="inlineCode" components={components} parentName="p">{`http://httpbin.org/post`}</MDXTag>{`, then select the object for which you want to create a webhook.`}</MDXTag>
</MDXTag>
<MDXTag name="li" components={components} parentName="ol">
<MDXTag name="p" components={components} parentName="li">{`Click Add Webhook.`}</MDXTag>
</MDXTag>
</MDXTag>
<MDXTag name="p" components={components}>{`You are directed to the newly created webhook detail page.`}</MDXTag>
<MDXTag name="hr" components={components}></MDXTag>
<MDXTag name="h2" components={components}>{`Testing a Webhook`}</MDXTag>
<MDXTag name="p" components={components}>{`You should test that your webhook is correctly implemented. To do so:`}</MDXTag>
<MDXTag name="ol" components={components}>
<MDXTag name="li" components={components} parentName="ol">{`On the webhook detail page, click Test. A test delivery request is generated.`}</MDXTag>
</MDXTag>
<MDXTag name="p" components={components}>{`The small, green ‘200 OK’ button corresponds to the HTTP status definition for a standard successful HTTP request. Any webhook delivery request that returns a number beginning with 2 (two) is considered a success.`}</MDXTag>
<MDXTag name="ol" components={components} props={{"start":2}}>
<MDXTag name="li" components={components} parentName="ol">{`Click ‘200 OK’. It expands, displaying the following information:`}</MDXTag>
</MDXTag>
<MDXTag name="p" components={components}>{`The `}<MDXTag name="strong" components={components} parentName="p">{`Request Headers`}</MDXTag>{` and `}<MDXTag name="strong" components={components} parentName="p">{`Request Payload`}</MDXTag>{` are what the Eventbrite webhooks system sent to the endpoint URL (i.e. your website). The `}<MDXTag name="strong" components={components} parentName="p">{`Request Payload`}</MDXTag>{` is valid JSON, which you can parse and use.`}</MDXTag>
<MDXTag name="p" components={components}>{`Conversely, the `}<MDXTag name="strong" components={components} parentName="p">{`Response Headers`}</MDXTag>{` and `}<MDXTag name="strong" components={components} parentName="p">{`Response Body`}</MDXTag>{` comprise the data sent back by the your receiving app. The delivery response is stored for 15 days.`}</MDXTag>
<MDXTag name="hr" components={components}></MDXTag>
<MDXTag name="h2" components={components}>{`Failed Webhooks`}</MDXTag>
<MDXTag name="p" components={components}>{`On occasion, a webhook delivery request returns a number that isn’t in the 2xx pattern, possibly because the target app responded with an error code, had too many redirects, or suffered from latency issues. When this occurs, the webhook system displays the request with a small red button with the appropriate error-level HTTP status code and message.`}</MDXTag>
<MDXTag name="p" components={components}>{`When a non-2xx patterned response occurs, the Eventbrite webhook system continues to resend the request every ten minutes, until the system either receives a success, or ten additional attempts are made. This is done to provide you with the opportunity to correct any problems with your app integration.`}</MDXTag>
<MDXTag name="p" components={components}>{`You can also manually resend a failed webhook delivery request. To do so:`}</MDXTag>
<MDXTag name="ol" components={components}>
<MDXTag name="li" components={components} parentName="ol">
<MDXTag name="p" components={components} parentName="li">{`Go to the `}<MDXTag name="a" components={components} parentName="p" props={{"href":"/account-settings/webhooks/"}}>{`webhook dashboard`}</MDXTag>{`.`}</MDXTag>
</MDXTag>
<MDXTag name="li" components={components} parentName="ol">
<MDXTag name="p" components={components} parentName="li">{`Click the red failure button. The button expands to display a a green ‘resend hook’ button.`}</MDXTag>
</MDXTag>
<MDXTag name="li" components={components} parentName="ol">
<MDXTag name="p" components={components} parentName="li">{`Click the green ‘resend hook’ button.`}</MDXTag>
</MDXTag>
</MDXTag>
<MDXTag name="h3" components={components}>{`Deleting Webhooks`}</MDXTag>
<MDXTag name="p" components={components}>{`You can delete a webhook. Please be aware that this is a permanent action that both deletes the actual webhook and all of it's delivery request and delivery response history.`}</MDXTag>
<MDXTag name="p" components={components}>{`To delete a webhook:`}</MDXTag>
<MDXTag name="ol" components={components}>
<MDXTag name="li" components={components} parentName="ol">
<MDXTag name="p" components={components} parentName="li">{`Go to the `}<MDXTag name="a" components={components} parentName="p" props={{"href":"/account-settings/webhooks/"}}>{`webhook dashboard`}</MDXTag>{`.`}</MDXTag>
</MDXTag>
<MDXTag name="li" components={components} parentName="ol">
<MDXTag name="p" components={components} parentName="li">{`Click the big blue ‘Delete’ button.`}</MDXTag>
</MDXTag>
<MDXTag name="li" components={components} parentName="ol">
<MDXTag name="p" components={components} parentName="li">{`Confirm that you want to delete the webhook, by clicking on the prompt.`}</MDXTag>
</MDXTag>
</MDXTag>
<NextSteps
  steps={[
      {
        title: "App OAuth Flow",
        link: "/platform/docs/app-oauth-flow",
      },
      {
        title: "Authentication",
        link: "/platform/docs/authentication",
      }
    ]}
/>
           </MDXTag>
  }
}
  